import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { fmt } from "../../lib/money";
import { Search, ExternalLink, DollarSign } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin view of every affiliate on the platform.
 * Quick lookup: paste a slug into the search box → see the owner's email,
 * name, click count, conversions, revenue, commission earned.
 */
export default function AdminAffiliates() {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("commission");

  useEffect(() => {
    api.get("/admin/affiliates?limit=500")
      .then((r) => setAffiliates(r.data.affiliates || []))
      .catch(() => toast.error("Couldn't load affiliates"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let rows = !needle ? affiliates : affiliates.filter((a) => (
      (a.affiliate_slug || "").toLowerCase().includes(needle)
      || (a.email || "").toLowerCase().includes(needle)
      || (a.name || "").toLowerCase().includes(needle)
    ));
    const cmp = {
      commission: (a, b) => (b.commission_total || 0) - (a.commission_total || 0),
      revenue: (a, b) => (b.revenue || 0) - (a.revenue || 0),
      conversions: (a, b) => (b.conversions || 0) - (a.conversions || 0),
      clicks: (a, b) => (b.clicks || 0) - (a.clicks || 0),
      name: (a, b) => (a.name || "").localeCompare(b.name || ""),
    }[sortBy] || (() => 0);
    return [...rows].sort(cmp);
  }, [affiliates, q, sortBy]);

  return (
    <div>
      <h1 className="font-display font-black text-4xl tracking-tight mb-2">Affiliates</h1>
      <p className="text-sm text-neutral-600 mb-6">Every user with a share slug. Paste any slug from the affiliate banner to find the owner.</p>

      <SlugForensics />

      <PendingPayouts onPaid={() => api.get("/admin/affiliates?limit=500").then((r) => setAffiliates(r.data.affiliates || []))} />

      <div className="grid sm:grid-cols-[1fr_auto] gap-2 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            data-testid="admin-affiliates-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by slug, email, or name…"
            className="w-full h-10 pl-9 pr-3 border-2 border-black rounded-none font-mono text-sm"
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          data-testid="admin-affiliates-sort"
          className="h-10 px-3 border-2 border-black rounded-none text-sm bg-white"
        >
          <option value="commission">Sort: commission</option>
          <option value="revenue">Sort: revenue</option>
          <option value="conversions">Sort: conversions</option>
          <option value="clicks">Sort: clicks</option>
          <option value="name">Sort: name</option>
        </select>
      </div>

      <div className="text-xs text-neutral-500 mb-2">
        {loading ? "Loading…" : `${filtered.length} of ${affiliates.length} affiliate${affiliates.length === 1 ? "" : "s"}`}
      </div>

      {!loading && filtered.length === 0 && (
        <div className="border border-neutral-200 p-6 text-center text-sm text-neutral-500">
          {q ? "No affiliates match." : "No affiliates yet."}
        </div>
      )}

      {filtered.length > 0 && (
        <div className="overflow-x-auto border border-neutral-200">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left text-[11px] uppercase tracking-widest text-neutral-500">
                <th className="px-3 py-2 font-semibold">Slug</th>
                <th className="px-3 py-2 font-semibold">Email</th>
                <th className="px-3 py-2 font-semibold">Name</th>
                <th className="px-3 py-2 font-semibold text-right">Clicks</th>
                <th className="px-3 py-2 font-semibold text-right">Conv.</th>
                <th className="px-3 py-2 font-semibold text-right">Revenue</th>
                <th className="px-3 py-2 font-semibold text-right">Commission</th>
                <th className="px-3 py-2 font-semibold w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.user_id} data-testid={`affiliate-row-${a.affiliate_slug}`} className="border-b border-neutral-100 last:border-b-0 hover:bg-[#F0F4FF]">
                  <td className="px-3 py-2.5 font-mono text-xs font-bold">{a.affiliate_slug}</td>
                  <td className="px-3 py-2.5 break-all">{a.email}</td>
                  <td className="px-3 py-2.5 text-neutral-600">{a.name}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{(a.clicks || 0).toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{(a.conversions || 0).toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{fmt(a.revenue || 0)}</td>
                  <td className={`px-3 py-2.5 text-right font-mono font-bold ${a.commission_total > 0 ? "text-[#00A355]" : ""}`}>
                    {fmt(a.commission_total || 0)}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm(`Mark all unpaid commission for ${a.email} as PAID?\n\nThis snapshots the current unpaid balance and resets it to G$0. Use after sending payment to the affiliate.`)) return;
                          try {
                            const { data } = await api.post(`/admin/affiliates/${encodeURIComponent(a.user_id)}/mark-paid`);
                            toast.success(`Marked ${fmt(data.amount)} paid · ${data.payout_id}`);
                            // trigger refresh
                            setAffiliates((rows) => rows.map((r) => r.user_id === a.user_id ? { ...r, _refresh: Date.now() } : r));
                            const refresh = await api.get("/admin/affiliates?limit=500");
                            setAffiliates(refresh.data.affiliates || []);
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Mark paid failed");
                          }
                        }}
                        data-testid={`mark-paid-${a.affiliate_slug}`}
                        title="Mark unpaid commission as paid out (resets balance)"
                        className="h-7 px-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-[#00A355] text-white hover:bg-[#008844]"
                      >
                        Pay out
                      </button>
                      <a
                        href={`/api/r/${a.affiliate_slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open this affiliate's share link"
                        className="text-[#0033FF] hover:text-black inline-flex"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function SlugForensics() {
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const inspect = async (e) => {
    e?.preventDefault?.();
    const s = slug.trim();
    if (!s) { toast.error("Paste a slug first"); return; }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.get(`/admin/slug/${encodeURIComponent(s)}`);
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lookup failed");
    } finally {
      setBusy(false);
    }
  };

  const badge = (cls) => {
    if (cls === "local_affiliate") return { label: "LOCAL AFFILIATE", color: "bg-[#00A355]" };
    if (cls === "external_trackmatrix") return { label: "EXTERNAL (TRACKMATRIX)", color: "bg-[#0033FF]" };
    return { label: "UNKNOWN / NEVER SEEN", color: "bg-[#FF2A00]" };
  };

  return (
    <div className="border-2 border-[#0033FF] bg-[#F0F4FF] p-4 mb-6">
      <div className="text-[11px] uppercase tracking-widest font-bold text-[#0033FF] mb-1">Slug forensics</div>
      <p className="text-xs text-neutral-700 mb-3">Investigate any slug — local or external. See where it came from, clicks, orders, and webhook history.</p>
      <form onSubmit={inspect} className="grid sm:grid-cols-[1fr_auto] gap-2">
        <input
          data-testid="slug-forensics-input"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="e.g. larry-morgan-new-d2a706"
          className="h-10 px-3 border-2 border-black rounded-none font-mono text-sm"
        />
        <button
          type="submit"
          data-testid="slug-forensics-btn"
          disabled={busy || !slug.trim()}
          className="h-10 px-4 bg-[#0033FF] hover:bg-[#0022CC] text-white text-sm font-semibold disabled:opacity-50"
        >
          {busy ? "Inspecting…" : "Inspect"}
        </button>
      </form>

      {result?.ok && (() => {
        const b = badge(result.classification);
        return (
          <div className="mt-3 bg-white border border-neutral-300 p-3">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <code className="font-mono text-sm font-bold">{result.slug}</code>
              <span className={`${b.color} text-white text-[10px] uppercase tracking-widest font-bold px-2 py-0.5`}>{b.label}</span>
            </div>
            {result.local_owner ? (
              <div className="mb-3 p-2 bg-[#F0FFF4] border border-[#00A355]/40">
                <div className="text-[11px] uppercase tracking-widest font-semibold text-[#00A355] mb-1">Local owner</div>
                <div className="text-sm font-medium">{result.local_owner.email}</div>
                <div className="text-xs text-neutral-600">{result.local_owner.name} · joined {new Date(result.local_owner.created_at).toLocaleDateString()}</div>
              </div>
            ) : (
              <div className="mb-3 p-2 bg-[#FFF5F2] border border-[#FF2A00]/40 text-xs text-[#FF2A00]">
                This slug is NOT a local affiliate. No user on guyanadiscounts.com owns it. It came from an external source (Trackmatrix link, document.referrer, or manual paste). Local commission won't roll up — Trackmatrix may still credit it externally.
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 text-center mb-3">
              <Stat label="Clicks" value={result.stats.clicks_total} />
              <Stat label="Orders" value={result.stats.orders_total} />
              <Stat label="Customers w/ slug" value={result.stats.customers_with_slug_stored} />
            </div>
            {result.recent_orders.length > 0 && (
              <Section title="Recent orders">
                {result.recent_orders.map((o) => (
                  <li key={o.order_id} className="text-xs font-mono py-1 border-b border-neutral-100 last:border-b-0 flex justify-between">
                    <span>{o.order_id}</span>
                    <span>{new Date(o.created_at).toLocaleDateString()} · G${o.total}</span>
                  </li>
                ))}
              </Section>
            )}
            {result.recent_clicks.length > 0 && (
              <Section title="Recent clicks">
                {result.recent_clicks.slice(0, 5).map((c) => (
                  <li key={c.click_id} className="text-xs py-1 border-b border-neutral-100 last:border-b-0">
                    <span className="font-mono">{new Date(c.ts).toLocaleString()}</span>
                    {c.product_id && <span className="text-neutral-500"> · {c.product_id}</span>}
                    {c.referer && <span className="text-neutral-400 ml-1 truncate inline-block max-w-[200px] align-bottom">from: {c.referer}</span>}
                  </li>
                ))}
              </Section>
            )}
            {result.customers.length > 0 && (
              <Section title="Customers who have this slug stored">
                {result.customers.map((c) => (
                  <li key={c.user_id} className="text-xs py-1 border-b border-neutral-100 last:border-b-0 flex justify-between">
                    <span>{c.email}</span>
                    {c.tm_ref_captured_at && <span className="text-neutral-500 font-mono">{new Date(c.tm_ref_captured_at).toLocaleDateString()}</span>}
                  </li>
                ))}
              </Section>
            )}
            {result.trackmatrix_log.length > 0 && (
              <Section title="Trackmatrix webhook history">
                {result.trackmatrix_log.map((entry, i) => (
                  <li key={i} className="text-xs py-1 border-b border-neutral-100 last:border-b-0 flex justify-between">
                    <span className="font-mono">{entry.order_id}</span>
                    <span className={entry.status_code >= 200 && entry.status_code < 300 ? "text-[#00A355]" : "text-[#FF2A00]"}>HTTP {entry.status_code || "?"}</span>
                  </li>
                ))}
              </Section>
            )}
          </div>
        );
      })()}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="p-2 bg-neutral-50">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="text-xl font-display font-black">{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-3">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500 mb-1">{title}</div>
      <ul className="border border-neutral-200">{children}</ul>
    </div>
  );
}


function PendingPayouts({ onPaid }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payouts?status=pending");
      setRequests(data.requests || []);
    } catch {
      toast.error("Couldn't load pending payouts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markPaid = async (req) => {
    if (!window.confirm(`Mark ${fmt(req.amount)} as PAID to ${req.email}?\n\nClick OK after sending the funds. This resets their unpaid balance to G$0.`)) return;
    try {
      const { data } = await api.post(`/admin/affiliates/${encodeURIComponent(req.user_id)}/mark-paid`);
      toast.success(`Paid ${fmt(data.amount)} · ${data.payout_id}`);
      load();
      onPaid?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mark paid failed");
    }
  };

  if (loading) return null;
  if (requests.length === 0) {
    return (
      <div className="mb-6 border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-500 flex items-center gap-2">
        <DollarSign className="w-3.5 h-3.5" />
        No pending payout requests right now. When affiliates click "Request payout" they'll show up here.
      </div>
    );
  }

  const totalPending = requests.reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <div className="mb-6 border-2 border-[#FFE600] bg-[#FFFBE6] p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#A07D00] mb-0.5">
            <DollarSign className="w-3.5 h-3.5 inline -mt-0.5 mr-0.5" />
            Pending payout requests
          </div>
          <div className="text-2xl font-display font-black">{requests.length} request{requests.length === 1 ? "" : "s"} · {fmt(totalPending)} total</div>
        </div>
      </div>
      <div className="space-y-2">
        {requests.map((r) => (
          <div key={r.request_id} className="bg-white border border-[#FFCC66] p-2.5 sm:p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`pending-payout-${r.request_id}`}>
            <div className="min-w-0">
              <div className="font-medium text-sm">{r.name}</div>
              <div className="text-[11px] text-neutral-600 break-all">{r.email} · <code className="font-mono">{r.slug}</code></div>
              <div className="text-[11px] text-neutral-500">Requested {new Date(r.requested_at).toLocaleString()}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="text-xl font-display font-black">{fmt(r.amount)}</div>
              <button
                type="button"
                onClick={() => markPaid(r)}
                data-testid={`pending-mark-paid-${r.request_id}`}
                className="h-9 px-4 bg-[#00A355] hover:bg-[#008844] text-white text-xs font-bold uppercase tracking-widest"
              >
                Mark paid
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
